import type { PointWithOffset } from '../types/types';

const BIT_DEPTH = 7;
const BIT_LIMIT: number = 1 << BIT_DEPTH;
const BIT_MASK: number = BIT_LIMIT - 1;
const WORD_TO_HEX: Array<string> = [];

export type PointWithOffsetTimeNumber = Omit<PointWithOffset, 'time'> & { time: number };
export type PointWithTrackOffset = PointWithOffsetTimeNumber & { isOnTrack: boolean };

function populateWordToHex() {
  if (WORD_TO_HEX.length === 0) {
    for (let n = 0; n <= 0xffff; ++n) {
      const hexQuartet = n.toString(16).padStart(4, '0');
      WORD_TO_HEX.push(hexQuartet);
    }
  }
}

// populateWordToHex has to be called before using this function
function readMongoIdUltraFast(view: DataView, offset: number): string {
  let out = '';
  for (let i = offset; i < offset + 12; i += 2) {
    out += WORD_TO_HEX[view.getUint16(i)];
  }
  return out;
}

function decodeValues(
  bytestream: DataView,
  offset: number,
  count: number,
  writer: (arg0: number, arg1: number) => void,
) {
  let i = offset;
  let j = 0;
  while (i < bytestream.byteLength && j < count) {
    let byte_count = 0;
    while (true) {
      // Die Bytes werden in Form von 7-bit Zahlen übertragen.
      // Die ersten Bytes, die zu einer zusammenhängenden Zahl gehören,
      // haben das Byte an Stelle 8 auf 1. Das letzte Byte einer Zahl hat
      // dieses Byte an Stelle 8 auf 0. Damit wird signalisiert, dass keine
      // weiteren Bytes zu der Zahl gehören. Wenn das Byte an Stelle 8 auf 1
      // gesetzt ist, ergibt a & 128 == 128.

      let byte = bytestream.getUint8(i);

      if ((byte & BIT_LIMIT) === BIT_LIMIT) {
        byte_count++;
        i++;
      } else {
        // Es wurde das letzte Byte einer Zahl erreicht. Die Zahl kann zusammengebaut werden.
        let value = 0n;
        // Bytes werden von hinten nach vorn zusammengebaut.
        for (let k = 0; k <= byte_count; k++) {
          // Bisherige Bits werden um 7 nach links verschoben.
          // Mit a & 127 werden die 7 letzten Bits ausgewählt.
          byte = bytestream.getUint8(i - k);
          value = (value << BigInt(BIT_DEPTH)) + BigInt(byte & BIT_MASK);
        }

        // Die Zahl enthält immer ein Vorzeichenbit an Stelle 1.
        // Dieses muss aus der fertigen Zahl entfernt werden.
        // Dadurch werden alle Bits um 1 nach rechts verschoben.
        // Ist es auf 1 gesetzt muss die Zahl anschließend negativ gemacht werden.
        if ((value & 1n) === 1n) {
          value = ~(value >> 1n);
        } else {
          value = value >> 1n;
        }

        // Zahl ist fertig zusammengebaut und wird zurückgegeben.
        writer(j, Number(value));
        i++;
        j++;
        break;
      }
    }
  }
}

function decodePointArray(
  view: DataView,
  byteOffset: number,
  trackLength: number,
  stepWidth: number,
): Array<PointWithOffsetTimeNumber> {
  const array = new Array(trackLength);
  let lat = 0;
  let lng = 0;
  let time = 0;
  let offset = 0;

  decodeValues(view, byteOffset, trackLength * 4, (i, v) => {
    const index = Math.floor(i / 4);
    let item: PointWithOffsetTimeNumber = {
      lat: 0,
      lng: 0,
      time: 0,
      offset: 0,
    };
    if (i % 4 === 0) {
      item = {
        lat: 0,
        lng: 0,
        time: 0,
        offset: 0,
      };
      array[index] = item;
    } else {
      item = array[index];
    }
    switch (i % 4) {
      case 0:
        // lat
        lat += v;
        item.lat = lat / 100000;
        break;
      case 1:
        // lng
        lng += v;
        item.lng = lng / 100000;
        break;
      case 2:
        // time
        time += v;
        item.time = time * stepWidth;
        break;
      case 3:
        // offset
        offset += v;
        item.offset = offset;
        break;
      default:
        throw new Error('Invalid state');
    }
  });
  return array;
}

function decodePointArrayBinarized(
  view: DataView,
  byteOffset: number,
  trackLength: number,
  stepWidth: number,
): Array<PointWithTrackOffset> {
  const array = new Array(trackLength);
  let lat = 0;
  let lng = 0;
  let time = 0;
  let offset = 0;

  decodeValues(view, byteOffset, trackLength * 5, (i, v) => {
    const index = Math.floor(i / 5);
    let item: PointWithTrackOffset = {
      lat: 0,
      lng: 0,
      time: 0,
      offset: 0,
      isOnTrack: false,
    };
    if (i % 5 === 0) {
      item = {
        lat: 0,
        lng: 0,
        time: 0,
        offset: 0,
        isOnTrack: false,
      };
      array[index] = item;
    } else {
      item = array[index];
    }
    switch (i % 5) {
      case 0:
        // lat
        lat += v;
        item.lat = lat / 100000;
        break;
      case 1:
        // lng
        lng += v;
        item.lng = lng / 100000;
        break;
      case 2:
        // time
        time += v;
        item.time = time * stepWidth;
        break;
      case 3:
        // offset
        offset += v;
        item.offset = offset;
        break;
      case 4:
        item.isOnTrack = v === 1;
        break;
      default:
        throw new Error('Invalid state');
    }
  });
  return array;
}

export function monotonicPush(
  a: Array<PointWithOffsetTimeNumber>,
  b: Array<PointWithOffsetTimeNumber>,
) {
  if (a.length === 0) {
    a.push(...b);
  } else {
    const aLastTime: number = a[a.length - 1].time;
    let bIndex = 0;
    for (; bIndex < b.length && b[bIndex].time <= aLastTime; bIndex++);
    a.push(...b.slice(bIndex));
  }
}

export function decodeBinary(buf: ArrayBuffer): Map<string, Array<PointWithOffsetTimeNumber>> {
  populateWordToHex();
  const output: Map<string, Array<PointWithOffsetTimeNumber>> = new Map();
  const view = new DataView(buf);

  if (view.byteLength === 0) return new Map();

  let offset = 0;
  const count: number = view.getUint32(offset, true);
  offset += 4;
  const stepWidth = view.getUint32(offset, true);
  offset += 4;
  for (let i = 0; i < count && offset < view.byteLength; i++) {
    const trackId = readMongoIdUltraFast(view, offset);
    offset += 12;
    const trackOffset = view.getUint32(offset, true);
    offset += 4;
    const trackLength = view.getUint32(offset, true);
    offset += 4;
    const array = decodePointArray(view, trackOffset, trackLength, stepWidth);
    output.set(trackId, array);
  }
  return output;
}

export function decodeBinaryBinarized(buf: ArrayBuffer): Map<string, Array<PointWithTrackOffset>> {
  populateWordToHex();
  const output: Map<string, Array<PointWithTrackOffset>> = new Map();
  const view = new DataView(buf);

  if (view.byteLength === 0) return new Map();

  let offset = 0;
  const count: number = view.getUint32(offset, true);
  offset += 4;
  const stepWidth = view.getUint32(offset, true);
  offset += 4;
  for (let i = 0; i < count && offset < view.byteLength; i++) {
    const trackId = readMongoIdUltraFast(view, offset);
    offset += 12;
    const trackOffset = view.getUint32(offset, true);
    offset += 4;
    const trackLength = view.getUint32(offset, true);
    offset += 4;
    const array = decodePointArrayBinarized(view, trackOffset, trackLength, stepWidth);
    output.set(trackId, array);
  }
  return output;
}
